'use client'
import { cn } from '@/util/cn'
import { ArrowPathIcon } from '@heroicons/react/24/solid'
import { RegisterLink } from '@kinde-oss/kinde-auth-nextjs'
import { TargetLanguageCode } from 'deepl-node'
import { useEffect, useState } from 'react'
import Header from '../header'
import DemoInput, { TooltipShow } from '../translate/demo-input'
import { TargetLanguage } from '../translate/types'
import LangTagsDemo from './lang-tags-demo'
import SpinnerButton from './spinner-button'
import TalkingHippo from './talking-hippo'
import { Divider } from './ui'

type Lang = TargetLanguage & {
    text: string
}

type Data = {
    autoNext?: number
    focus?: number
    description?: string
    langOrder: Lang[]
    comments?: string
    tooltip?: TooltipShow
    done?: boolean
}

const step0: Data = {
    description: 'Click to see a demo 👆',
    langOrder: [
        {
            code: 'en-US',
            name: 'English',
            text: 'Hello',
        },
        {
            code: 'es',
            name: 'Spanish',
            text: 'Hola',
        },
        {
            code: 'fr',
            name: 'French',
            text: 'Bonjour',
        },
        {
            code: 'de',
            name: 'German',
            text: 'Hallo',
        },
        {
            code: 'pt-BR',
            name: 'Portuguese',
            text: 'Olá',
        },
    ],
} as const

const step1: Data = {
    description: 'Continue',
    langOrder: step0.langOrder,
    focus: 0,
    comments: 'Type in one language, translate to the others',
    autoNext: 4000,
}

const step2: Data = {
    description: 'Continue',
    langOrder: step0.langOrder,
    focus: 2,
    autoNext: 2000,
    comments: 'You can select and edit any of the languages',
}

const step2a1: Data = {
    ...step2,
    langOrder: step0.langOrder.map(changeText('Bonjou')),
    autoNext: 400,
}

const step2a2: Data = {
    ...step2,
    langOrder: step0.langOrder.map(changeText('Bonjo')),
    autoNext: 250,
}

const step2a3: Data = {
    ...step2,
    langOrder: step0.langOrder.map(changeText('Bonj')),
    autoNext: 150,
}

const step2b: Data = {
    ...step2,
    langOrder: step0.langOrder.map(changeText('')),
    autoNext: 500,
}

const step3: Data = {
    ...step2,
    langOrder: step0.langOrder.map(changeText('M')),
    autoNext: 250,
}

const step3b: Data = {
    ...step3,
    langOrder: step0.langOrder.map(changeText('Me')),
    autoNext: 150,
}

const step3c: Data = {
    ...step3,
    langOrder: step0.langOrder.map(changeText('Mer')),
    autoNext: 150,
}

const step3d: Data = {
    ...step3,
    langOrder: step0.langOrder.map(changeText('Merc')),
    autoNext: 100,
}

const step3e: Data = {
    ...step3,
    langOrder: step0.langOrder.map(changeText('Merci')),
    autoNext: 1000,
}

const step3f: Data = {
    ...step3,
    langOrder: [
        {
            code: 'en-US',
            name: 'English',
            text: 'Thank you',
        },
        {
            code: 'es',
            name: 'Spanish',
            text: 'Gracias',
        },
        {
            code: 'fr',
            name: 'French',
            text: 'Merci',
        },
        {
            code: 'de',
            name: 'German',
            text: 'Danke',
        },
        {
            code: 'pt-BR',
            name: 'Portuguese',
            text: 'Obrigado',
        },
    ],
    autoNext: 500,
}

const step3g: Data = {
    ...step3f,
    autoNext: 1000,
    focus: undefined,
}

const step4: Data = {
    description: 'Get started',
    comments: 'Useful buttons - clear, copy, bookmark, audio and reorder',
    langOrder: [
        {
            code: 'en-US',
            name: 'English',
            text: 'How are you?',
        },
        {
            code: 'es',
            name: 'Spanish',
            text: '¿Cómo estás hoy?',
        },
        {
            code: 'it',
            name: 'Italian',
            text: 'Come sta oggi?',
        },
        {
            code: 'de',
            name: 'German',
            text: 'Wie geht es Ihnen heute?',
        },
    ],
    autoNext: 3000,
}

const step4a: Data = {
    ...step4,
    focus: 2,
    autoNext: 1000,
}

const step4a2: Data = {
    ...step4a,
    tooltip: 'clear',
}
const step4b: Data = {
    ...step4a,
    tooltip: 'copy',
}
const step4c: Data = {
    ...step4a,
    tooltip: 'bookmark',
}
const step4Audio: Data = {
    ...step4a,
    tooltip: 'audio',
}
const step4d: Data = {
    ...step4a,
    tooltip: 'reorder',
}

const step4e: Data = {
    ...step4a,
    tooltip: undefined,
    autoNext: 1000,
}

const step4f: Data = {
    ...step4a,
    focus: undefined,
    tooltip: undefined,
    autoNext: 1000,
}

const step5: Data = {
    description: 'Get started',
    langOrder: [
        {
            code: 'en-US',
            name: 'English',
            text: "Let's go!",
        },
        {
            code: 'es',
            name: 'Spanish',
            text: '¡Vamos!',
        },
        {
            code: 'fr',
            name: 'French',
            text: 'Allons-y!',
        },
        {
            code: 'it',
            name: 'Italian',
            text: 'Andiamo!',
        },
        {
            code: 'de',
            name: 'German',
            text: "Los geht's!",
        },
    ],
    comments: 'More features on the way! Sign up now!',
    done: true,
}

const demos = [
    step0,
    step1,
    step2,
    step2a1,
    step2a2,
    step2a3,
    step2b,
    step3,
    step3b,
    step3c,
    step3d,
    step3e,
    step3f,
    step3g,
    step4,
    step4a,
    step4a2,
    step4b,
    step4c,
    step4Audio,
    step4d,
    step4e,
    step4f,
    step5,
]

const TranslationList = () => {
    const [stepIndex, setStepIndex] = useState(0)
    const [hasSeen, setHasSeen] = useState(false)
    const currentStep = demos[stepIndex]
    const prevData = demos[stepIndex - 1]
    const [inputIndex, setInputIndex] = useState(1)
    const tl = currentStep.langOrder.map((l) => l.code)
    function nextStep() {
        const demoIndex = stepIndex === demos.length - 1 ? 0 : stepIndex + 1
        setInputIndex(0)
        setStepIndex(demoIndex)
    }

    useEffect(() => {
        let timeout: NodeJS.Timeout
        if (currentStep.autoNext) {
            timeout = setTimeout(() => {
                setStepIndex(stepIndex + 1)
            }, currentStep.autoNext)
        }
        return () => clearTimeout(timeout)
    }, [currentStep.autoNext, stepIndex])

    // const [focusedLang, setFocusedLang] = useState<TargetLanguageCode>()
    const numberOfLangs = currentStep.langOrder.length
    useEffect(() => {
        const speed = 300
        const timeout = setTimeout(
            () => {
                if (inputIndex < numberOfLangs) {
                    setInputIndex((index) => {
                        return index + 1
                    })
                }
            },
            Math.max(speed - numberOfLangs * 40, 100)
        )
        return () => clearTimeout(timeout)
    }, [numberOfLangs, inputIndex])
    return (
        <div className="flex flex-col items-center gap-6">
            <div className="mockup-phone shadow-2xl shadow-black">
                <div className="bg-base-100 mockup-phone-display flex h-full w-full flex-col overflow-hidden">
                    <div
                        onClick={(e) => {
                            if (!currentStep.done) {
                                e.stopPropagation()
                                e.preventDefault()
                                nextStep()
                            }
                        }}
                        className={cn(
                            'group relative flex h-[620px] w-[390px] flex-col self-center rounded-lg px-6 text-left md:h-[640px] md:w-[390px]',
                            currentStep.autoNext ? 'cursor-default' : 'cursor-pointer',
                            currentStep.done && 'cursor-default'
                        )}
                    >
                        <Header className="justify-center pt-6" showMenu />
                        <div className="flex flex-1 flex-col justify-between p-6 shadow-2xl">
                            <div className="flex flex-col gap-2">
                                {tl.map((targetLang: TargetLanguageCode, index: number) => {
                                    const text =
                                        index <= inputIndex
                                            ? currentStep.langOrder[index].text
                                            : prevData?.langOrder[index]?.text || ''
                                    return (
                                        <DemoInput
                                            tooltip={currentStep.tooltip}
                                            textValue={text}
                                            key={`${targetLang}`}
                                            setFocusedLang={() => {}}
                                            targetLanguage={targetLang}
                                            index={index}
                                            focusedLang={index === currentStep.focus ? targetLang : undefined}
                                            isFetching={true}
                                        />
                                    )
                                })}
                            </div>
                            {currentStep.comments && <TalkingHippo text={currentStep.comments} />}
                            <div className="flex flex-col gap-1">
                                <Divider className="mt-0" />
                                <h4>Favorite Languages</h4>
                                <LangTagsDemo ordered={tl} langOrder={currentStep.langOrder} />
                            </div>
                        </div>
                        {stepIndex === 0 && !hasSeen && (
                            <div className="bg-base-100 absolute inset-0 flex items-center justify-center bg-opacity-75 text-center md:hidden">
                                <div
                                    className="hover:text-primary flex cursor-pointer flex-col justify-center"
                                    onClick={() => {
                                        setHasSeen(true)
                                        nextStep()
                                    }}
                                >
                                    <button className={'btn btn-primary mx-10'}>Click to see a demo</button>
                                </div>
                            </div>
                        )}
                        {currentStep.done && (
                            <div className="md:opacity-0 md:group-hover:opacity-100">
                                <div className="display bg-base-100 absolute inset-0 flex flex-col items-center justify-center gap-2 bg-opacity-75 text-center">
                                    <RegisterLink>
                                        <SpinnerButton
                                            label="Get Started"
                                            className="btn-primary w-48 cursor-pointer"
                                        />
                                    </RegisterLink>
                                    <div
                                        className="btn btn-outline flex w-48 cursor-pointer"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            setStepIndex(1)
                                        }}
                                    >
                                        <div>Watch Again</div>
                                        <ArrowPathIcon height={20} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="hidden md:block">
                {!currentStep.done ? (
                    <div
                        className={cn(`btn btn-outline`, currentStep.autoNext && 'hidden')}
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            nextStep()
                        }}
                    >
                        {currentStep.description}
                    </div>
                ) : (
                    <RegisterLink className={cn(`btn btn-primary`)}>{currentStep.description}</RegisterLink>
                )}
            </div>
        </div>
    )
}

export default TranslationList

function changeText(text: string): (value: Lang, index: number, array: Lang[]) => Lang {
    return (entry: Lang) => {
        if (entry.code === 'fr') {
            return { ...entry, text }
        }
        return entry
    }
}
